import {
  Badge,
  Heading,
  HStack,
  IconButton,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useCallback, useState } from "react";
import { useMemo } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { VideoCategory } from "../../types";
import { Table, Thead, Tbody, Tr, Th, Td } from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { VideoCategoryModal } from "../../components/VideoCategoryModal";

export const VimeoVideoCategoriesForm: React.FC = observer(() => {
  const { session } = useStores();
  const toast = useToast();
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [videoCategory, setVideoCategory] = useState<VideoCategory | null>();

  useEffect(() => {
    session.getVimeoVideoCategories();
    session.getVideoCategories();
  }, []);

  const videoCategories = useMemo(() => {
    const list = session.vimeoVideoCategories.map((vimeoVideoCategory) => {
      let videoCategoryItem = session.videoCategories.find(
        (videoCategory) =>
          videoCategory.resourceKey === vimeoVideoCategory.resourceKey
      );

      if (!videoCategoryItem) {
        return {
          name: vimeoVideoCategory.name,
          resourceKey: vimeoVideoCategory.resourceKey,
          externalId: vimeoVideoCategory.projectId,
        };
      }
      return videoCategoryItem;
    });

    list.sort((a, b) => {
      return (a.order ?? 999) - (b.order ?? 999);
    });

    return list;
  }, [session.videoCategories, session.vimeoVideoCategories]);

  const handleSubmit = useCallback(
    async (videoCategory: VideoCategory) => {
      try {
        if (videoCategory.id) {
          await session.updateVideoCategories(videoCategory);
        } else {
          await session.addVideoCategories(videoCategory);
        }
        setIsModalVisible(false);
        toast({
          title: t("common.actionExecutedSuccessfully"),
          position: "bottom",
          status: "success",
        });
      } catch (err) {
        toast({
          title: t("common.error"),
          position: "bottom",
          status: "error",
        });
      }
    },
    [session, t, toast]
  );

  return (
    <Stack>
      <Heading as="h5" size="sm" marginBottom={"1rem"}>
        {t("screens.adminSettings.vimeo")}
      </Heading>
      <Heading as="h6" size="xs">
        {t("screens.adminSettings.vimeoVideoCategories")}
      </Heading>

      <HStack gridGap={"1"} marginBottom={"2rem"} marginTop={"1rem"}>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>
                {t("screens.adminSettings.vimeoVideoCategoriesForm.vimeoDoc")}
              </Th>
              <Th>
                {t("screens.adminSettings.vimeoVideoCategoriesForm.state")}
              </Th>
              <Th>
                {t("screens.adminSettings.vimeoVideoCategoriesForm.title")}
              </Th>
              <Th>
                {t(
                  "screens.adminSettings.vimeoVideoCategoriesForm.description"
                )}
              </Th>
              <Th>
                {t("screens.adminSettings.vimeoVideoCategoriesForm.order")}
              </Th>
              <Th>{t("common.edit")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {videoCategories.map((item) => (
              <Tr key={item.resourceKey}>
                <Td>{item.name}</Td>
                <Td>
                  {item.enabled ? (
                    <Badge colorScheme="green">
                      {t(
                        "screens.adminSettings.vimeoVideoCategoriesForm.badgeActive"
                      )}
                    </Badge>
                  ) : (
                    <Badge>
                      {t(
                        "screens.adminSettings.vimeoVideoCategoriesForm.badgeNotActive"
                      )}
                    </Badge>
                  )}
                </Td>
                <Td>{item.title}</Td>
                <Td>{item.description}</Td>
                <Td>{item.order}</Td>
                <Td>
                  <IconButton
                    aria-label="edit"
                    variant="ghost"
                    colorScheme="black"
                    fontSize="20px"
                    icon={<EditIcon />}
                    onClick={() => {
                      setVideoCategory(item);
                      setIsModalVisible(true);
                    }}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <VideoCategoryModal
          isOpen={isModalVisible}
          videoCategory={videoCategory}
          onClose={() => {
            setIsModalVisible(false);
          }}
          onSubmit={handleSubmit}
        />
      </HStack>
    </Stack>
  );
});
